import api from './client';
import type { ApiResponse } from '@gokrew/models/generic';
import type {
  AssignTemporaryGroupPayload,
  BulkReportingLinesPayload,
  BulkReportingLinesResult,
  OrgChartNode,
  ReportingHistoryEntry,
  ReportingLineView,
  ReportingPerson,
  ReportingSuggestion,
  SetReportingLinePayload,
  TemporaryGroup,
  TemporaryGroupMoveResult,
  TemporaryGroupRefPayload,
} from '@gokrew/models/reporting';

/* ---------------------------------- */
/* 🔧 Endpoints */
/* ---------------------------------- */

const linesBase = (companyId: string) =>
  `/companies/${companyId}/reporting-lines`;

const endpoints = {
  line: (companyId: string, employeeId: string) =>
    `/companies/${companyId}/employees/${employeeId}/reporting-line`,
  history: (companyId: string, employeeId: string) =>
    `/companies/${companyId}/employees/${employeeId}/reporting-line/history`,
  bulk: (companyId: string) => `${linesBase(companyId)}/bulk`,
  suggestions: (companyId: string) => `${linesBase(companyId)}/suggestions`,
  orgChart: (companyId: string) => `${linesBase(companyId)}/org-chart`,
  managerOptions: (companyId: string) =>
    `${linesBase(companyId)}/manager-options`,
  temporaryGroups: (companyId: string) =>
    `${linesBase(companyId)}/temporary-groups`,
  assignTemporaryGroup: (companyId: string) =>
    `${linesBase(companyId)}/temporary-groups/assign`,
  makeTemporaryGroupPermanent: (companyId: string) =>
    `${linesBase(companyId)}/temporary-groups/make-permanent`,
};

/* ---------------------------------- */
/* 👤 One employee */
/* ---------------------------------- */

export const getReportingLine = async (
  companyId: string,
  employeeId: string
) => {
  const { data } = await api.get<ApiResponse<ReportingLineView>>(
    endpoints.line(companyId, employeeId)
  );
  return data;
};

export const setReportingLine = async (
  companyId: string,
  employeeId: string,
  payload: SetReportingLinePayload
) => {
  const { data } = await api.put<ApiResponse<ReportingLineView>>(
    endpoints.line(companyId, employeeId),
    payload
  );
  return data;
};

export const getReportingLineHistory = async (
  companyId: string,
  employeeId: string
) => {
  const { data } = await api.get<ApiResponse<ReportingHistoryEntry[]>>(
    endpoints.history(companyId, employeeId)
  );
  return data;
};

/* ---------------------------------- */
/* 🏢 Company structure */
/* ---------------------------------- */

export const getOrgChart = async (companyId: string) => {
  const { data } = await api.get<ApiResponse<OrgChartNode[]>>(
    endpoints.orgChart(companyId)
  );
  return data;
};

export const getReportingSuggestions = async (companyId: string) => {
  const { data } = await api.get<ApiResponse<ReportingSuggestion[]>>(
    endpoints.suggestions(companyId)
  );
  return data;
};

export const applyReportingLines = async (
  companyId: string,
  payload: BulkReportingLinesPayload
) => {
  const { data } = await api.post<ApiResponse<BulkReportingLinesResult>>(
    endpoints.bulk(companyId),
    payload
  );
  return data;
};

export const getManagerOptions = async (
  companyId: string,
  params: { search?: string; excludeEmployeeId?: string; limit?: number }
) => {
  const { data } = await api.get<ApiResponse<ReportingPerson[]>>(
    endpoints.managerOptions(companyId),
    { params }
  );
  return data;
};

/* ---------------------------------- */
/* 👥 Temporary groups (spec 3.4) */
/* ---------------------------------- */

export const getTemporaryGroups = async (companyId: string) => {
  const { data } = await api.get<ApiResponse<TemporaryGroup[]>>(
    endpoints.temporaryGroups(companyId)
  );
  return data;
};

/** Moves a group (all or some members) to a new manager in one step. */
export const assignTemporaryGroup = async (
  companyId: string,
  payload: AssignTemporaryGroupPayload
) => {
  const { data } = await api.post<ApiResponse<TemporaryGroupMoveResult>>(
    endpoints.assignTemporaryGroup(companyId),
    payload
  );
  return data;
};

/** The covering manager becomes the group's permanent manager. */
export const makeTemporaryGroupPermanent = async (
  companyId: string,
  payload: TemporaryGroupRefPayload
) => {
  const { data } = await api.post<ApiResponse<TemporaryGroupMoveResult>>(
    endpoints.makeTemporaryGroupPermanent(companyId),
    payload
  );
  return data;
};
